// The lights the eye's SG specular reflects, taken from the LIVE scene.

import { vector3 } from '../math/index.ts'
import type { Vector3 } from '../math/index.ts'

/** Unit directions surface→light with a linear-RGB radiance each. */
export interface EyeLightSet {
  directions: Float32Array  // count*3, unit, world
  colors: Float32Array      // count*3, linear rgb
  count: number
}

export const EMPTY_LIGHT_SET: EyeLightSet = {
  directions: new Float32Array(0), colors: new Float32Array(0), count: 0,
}

// Rec. 709 luma, used only to rank lights when there are more than the shader holds.
const luminance = (red: number, green: number, blue: number) =>
  0.2126 * red + 0.7152 * green + 0.0722 * blue

function worldPosition(object: any): Vector3 | null {
  const elements = object?.matrixWorld?.elements
  return elements ? [elements[12], elements[13], elements[14]] : null
}

interface Candidate { direction: Vector3, rgb: Vector3, weight: number }

const weighted = (direction: Vector3, rgb: Vector3): Candidate =>
  ({ direction, rgb, weight: luminance(rgb[0], rgb[1], rgb[2]) })

// Clamped at a 1 cm radius so a light dropped on the eye cannot divide by ~0.
const MIN_SQUARED_DISTANCE = 1e-4

// three aims a directional from its position at its target, so reverse it; no falloff.
function directionalCandidate(light: any, position: Vector3, target: Vector3): Candidate | null {
  const towardsLight = vector3.subtract(position, target)
  if (!(vector3.length(towardsLight) > 1e-9)) return null
  const { color, intensity } = light
  return weighted(vector3.normalize(towardsLight),
    [color.r * intensity, color.g * intensity, color.b * intensity])
}

// Inside three's own distance cutoff and spot cone, else the light does not reach here at all.
function punctualCandidate(light: any, position: Vector3, target: Vector3,
                           at: Vector3): Candidate | null {
  const towardsLight = vector3.subtract(position, at)
  const squaredDistance = Math.max(MIN_SQUARED_DISTANCE, vector3.dot(towardsLight, towardsLight))
  const distance = Math.sqrt(squaredDistance)
  const direction: Vector3 = vector3.scale(towardsLight, 1 / distance)

  const range = Number(light.distance)
  if (Number.isFinite(range) && range > 0 && distance > range) return null
  if (light.isSpotLight && !insideCone(light, direction, position, target)) return null

  const falloff = Number(light.intensity) / squaredDistance
  const { color } = light
  return weighted(direction, [color.r * falloff, color.g * falloff, color.b * falloff])
}

function insideCone(light: any, direction: Vector3, position: Vector3, target: Vector3): boolean {
  const aim = vector3.subtract(target, position)
  const aimLength = vector3.length(aim)
  if (!(aimLength > 1e-9)) return false
  const cosAngle = -vector3.dot(direction, aim) / aimLength
  return cosAngle >= Math.cos(Number(light.angle) || 0)
}

// Ambient and hemisphere have no direction, so no lobe can reflect them: not a candidate.
function candidate(light: any, at: Vector3): Candidate | null {
  if (!light || light.visible === false) return null
  if (!light.color) return null
  const intensity = Number(light.intensity)
  if (!Number.isFinite(intensity) || intensity <= 0) return null
  const position = worldPosition(light)
  if (!position) return null
  const target = (light.target && worldPosition(light.target)) || [0, 0, 0]

  if (light.isDirectionalLight) return directionalCandidate(light, position, target)
  if (light.isPointLight || light.isSpotLight) {
    return punctualCandidate(light, position, target, at)
  }
  return null
}

/** Every directional/point/spot light in `root`, evaluated at `at`; past `max` the DIMMEST are
 *  dropped and the count reported. */
export function collectSceneLights(root: any, at: Vector3, max = 8):
    EyeLightSet & { dropped: number } {
  const found: Candidate[] = []
  const visit = (object: any) => {
    if (!object || object.visible === false) return
    const lit = candidate(object, at)
    if (lit) found.push(lit)
    for (const child of object.children ?? []) visit(child)
  }
  visit(root)
  found.sort((first, second) => second.weight - first.weight)
  const dropped = Math.max(0, found.length - max)
  const kept = Math.min(found.length, max)
  const set = {
    directions: new Float32Array(kept * 3),
    colors: new Float32Array(kept * 3),
    count: kept,
    dropped,
  }
  for (let light = 0; light < kept; light++) {
    set.directions.set(found[light].direction, light * 3)
    set.colors.set(found[light].rgb, light * 3)
  }
  return set
}

/** The lights the checkpoint was trained under: the set that reproduces the trainer. */
export function bakedLightSet(spec: BakedSpecularSpec): EyeLightSet {
  const count = spec.nLights | 0
  const set = {
    directions: new Float32Array(count * 3),
    colors: new Float32Array(count * 3),
    count,
  }
  for (let light = 0; light < count; light++) {
    for (let axis = 0; axis < 3; axis++) {
      set.directions[light * 3 + axis] = spec.lightDirs[light * 3 + axis]
      set.colors[light * 3 + axis] = spec.lightIntensity[light]
    }
  }
  return set
}

/** eye_meta's `spec` block — the trained SG lights. */
export interface BakedSpecularSpec {
  nLights: number
  lightDirs: ArrayLike<number>
  lightIntensity: ArrayLike<number>
}

/** Which set the glint came from, so a panel or a test can say rather than guess. */
export type EyeLightSource = 'scene' | 'baked' | 'none'

/** The lights to shade with: the LIVE scene's whenever it has any, else the checkpoint's baked
 *  set, else none. Why the fallback is not a default: docs/OGS_EYES.md. */
export function resolveEyeLights(root: any, at: Vector3, baked: BakedSpecularSpec | null,
                                 max = 8): EyeLightSet & { dropped: number, source: EyeLightSource } {
  const scene = collectSceneLights(root, at, max)
  if (scene.count > 0) return { ...scene, source: 'scene' }
  if (baked && (baked.nLights | 0) > 0) {
    return { ...bakedLightSet(baked), dropped: 0, source: 'baked' }
  }
  return { ...EMPTY_LIGHT_SET, dropped: 0, source: 'none' }
}
